/* WeQuote Dev Board — Overview analytics (verify §30). Pure ports of the
 * period filter (ovYear/ovMonth), completed/requests-in-period, the
 * completed-by-dev breakdown, donut geometry (pieSvg → slice paths the view
 * renders as SVG), and the year list. */
import type { Feature } from './types'
import { DL, devColor } from './constants'

export const OV_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
export type OvMonth = number | 'all'

export function inPeriod(iso: string, ovYear: number, ovMonth: OvMonth): boolean {
  if (!iso) return false
  const p = ('' + iso).split('-')
  if (p.length !== 3) return false
  if (+p[0] !== ovYear) return false
  if (ovMonth === 'all') return true
  return +p[1] - 1 === ovMonth
}
export function completedInPeriod(features: Feature[], ovYear: number, ovMonth: OvMonth): Feature[] {
  return features.filter((f) => f.status === 'live' && f.completedOn && inPeriod(f.completedOn, ovYear, ovMonth))
}
export function requestsInPeriod(features: Feature[], ovYear: number, ovMonth: OvMonth): Feature[] {
  return features.filter((f) => f.dateAdded && inPeriod(f.dateAdded, ovYear, ovMonth))
}
export function completedByDev(list: Feature[]): Record<string, number> {
  const m: Record<string, number> = {}
  list.forEach((f) => {
    const d = f.dev && DL[f.dev] ? f.dev : 'unassigned'
    m[d] = (m[d] || 0) + 1
  })
  return m
}
export function overviewYears(features: Feature[], currentYear: number): number[] {
  const yrs: Record<number, 1> = {}
  features.forEach((f) => {
    ;[f.completedOn, f.dateAdded].forEach((d) => {
      if (d) { const y = parseInt(('' + d).slice(0, 4), 10); if (y) yrs[y] = 1 }
    })
  })
  yrs[currentYear] = 1
  return Object.keys(yrs).map(Number).sort((a, b) => b - a)
}

export interface PieSlice { key: string; d: string; color: string; n: number }
export interface Pie { total: number; single: boolean; color: string; slices: PieSlice[] }
/** Donut geometry (cx/cy 60, r 54, inner 32) — total 0 means "no pie". */
export function pieSlices(counts: Record<string, number>): Pie {
  const keys = Object.keys(counts).filter((k) => counts[k] > 0)
  const total = keys.reduce((s, k) => s + counts[k], 0)
  if (!total) return { total: 0, single: false, color: '', slices: [] }
  const cx = 60, cy = 60, r = 54
  if (keys.length === 1) return { total, single: true, color: devColor(keys[0]), slices: [{ key: keys[0], d: '', color: devColor(keys[0]), n: counts[keys[0]] }] }
  let a0 = -Math.PI / 2
  const slices: PieSlice[] = keys.map((k) => {
    const frac = counts[k] / total
    const a1 = a0 + frac * 2 * Math.PI
    const large = frac > 0.5 ? 1 : 0
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1)
    const d = 'M ' + cx + ' ' + cy + ' L ' + x0.toFixed(2) + ' ' + y0.toFixed(2) + ' A ' + r + ' ' + r + ' 0 ' + large + ' 1 ' + x1.toFixed(2) + ' ' + y1.toFixed(2) + ' Z'
    a0 = a1
    return { key: k, d, color: devColor(k), n: counts[k] }
  })
  return { total, single: false, color: '', slices }
}
